import type {
  FitnessPlanProgram,
  ProgramGenerationProgress,
} from './fitness-plan.domain';
import type {
  ProgramGenJob,
  ProgramGenJobRecord,
} from './plan-program-gen-job.service';
import { inferredFailureStep } from './program-build.helpers';

export function isProgramGenJobInFlight(
  status: ProgramGenJob['status'] | undefined,
): boolean {
  return status === 'queued' || status === 'running';
}

function programRowLooksInFlight(program: FitnessPlanProgram): boolean {
  const gen = program.generation?.status;
  return (
    gen === 'queued' ||
    gen === 'running' ||
    program.generationStatus === 'processing'
  );
}

export function isProgramGenerationFailed(
  program: FitnessPlanProgram,
): boolean {
  if (
    program.generation?.status === 'failed' ||
    program.generationStatus === 'error'
  ) {
    return true;
  }
  const fromGeneration = program.generation?.error?.trim();
  if (fromGeneration && !programRowLooksInFlight(program)) {
    return true;
  }
  const fromRow = program.generationError?.trim();
  if (fromRow && !programRowLooksInFlight(program)) {
    return true;
  }
  return false;
}

/** Block workouts, split, and sessions — overview stays open for status/errors. */
export function isProgramContentBlocked(
  program: FitnessPlanProgram,
  activeJob?: Pick<ProgramGenJob, 'status'> | null,
): boolean {
  return (
    isProgramGenerationFailed(program) ||
    isProgramGenerationActive(program, activeJob)
  );
}

/** @deprecated Use {@link isProgramContentBlocked}. */
export function isProgramGenerationBlocked(
  program: FitnessPlanProgram,
  activeJob?: Pick<ProgramGenJob, 'status'> | null,
): boolean {
  return isProgramContentBlocked(program, activeJob);
}

/** True while AI program generation is still running for this program row. */
export function isProgramGenerationActive(
  program: FitnessPlanProgram,
  activeJob?: Pick<ProgramGenJob, 'status'> | null,
): boolean {
  if (isProgramGenerationFailed(program)) {
    return false;
  }
  if (activeJob && isProgramGenJobInFlight(activeJob.status)) {
    return true;
  }
  const gen = program.generation?.status;
  if (gen === 'queued' || gen === 'running') {
    return true;
  }
  return program.generationStatus === 'processing';
}

export function activeGenJobsByProgramId(
  jobs: readonly ProgramGenJobRecord[],
): Map<string, ProgramGenJobRecord> {
  const map = new Map<string, ProgramGenJobRecord>();
  for (const job of jobs) {
    const programId = job.programId?.trim();
    if (!programId) {
      continue;
    }
    map.set(programId, job);
  }
  return map;
}

/** Apply a terminal Firestore job result onto a program row for display / client save. */
export function applyProgramGenJobToProgram(
  program: FitnessPlanProgram,
  job: Pick<ProgramGenJob, 'status' | 'error' | 'result'>,
): FitnessPlanProgram {
  if (job.status === 'error') {
    const msg = (job.error ?? 'Program generation failed.').trim();
    const errorText = msg || 'Program generation failed.';
    const inferredStep = inferredFailureStep(program) ?? 'goal_profile';
    const generation: ProgramGenerationProgress = {
      ...(program.generation ?? {
        progressPercent: 0,
        message: errorText,
      }),
      currentStep: program.generation?.currentStep ?? inferredStep,
      status: 'failed',
      error: errorText,
      message: errorText,
    };
    return {
      ...program,
      generation,
      generationStatus: 'error',
      generationError: errorText,
      generationJobId: undefined,
    };
  }
  if (job.status === 'done') {
    const workouts = Array.isArray(job.result?.workouts)
      ? job.result!.workouts
      : [];
    if (workouts.length === 0) {
      const errorText = 'Program generation finished with no workouts.';
      const generation: ProgramGenerationProgress = {
        ...(program.generation ?? {
          currentStep: 'finalising',
          progressPercent: 0,
          message: errorText,
        }),
        status: 'failed',
        error: errorText,
        message: errorText,
      };
      return {
        ...program,
        generation,
        generationStatus: 'error',
        generationError: errorText,
        generationJobId: undefined,
      };
    }
    const split =
      job.result?.split && typeof job.result.split === 'object'
        ? {
            ...program.split,
            ...(job.result.split as FitnessPlanProgram['split']),
          }
        : program.split;
    return {
      ...program,
      workouts: workouts as FitnessPlanProgram['workouts'],
      split,
      generationStatus: 'ready',
      generationJobId: undefined,
      generationError: undefined,
    };
  }
  return program;
}

/** Best available generation failure text (Firestore job beats stale plan row). */
export function programGenerationErrorMessage(
  program: FitnessPlanProgram,
  job?: Pick<ProgramGenJob, 'error'> | null,
): string {
  const fromJob = job?.error?.trim() ?? '';
  const fromProgram = program.generationError?.trim() ?? '';
  if (fromJob) {
    return fromJob;
  }
  if (fromProgram) {
    return fromProgram;
  }
  return 'Program generation failed.';
}

/** Align plan program rows with live Firestore generation jobs after a refetch. */
export function reconcileProgramsWithActiveGenJobs(
  programs: FitnessPlanProgram[],
  jobs: readonly ProgramGenJobRecord[],
  linkedJobsByProgramId: ReadonlyMap<string, ProgramGenJob> = new Map(),
  terminalJobsByProgramId: ReadonlyMap<string, ProgramGenJob> = new Map(),
): FitnessPlanProgram[] {
  const activeByProgram = activeGenJobsByProgramId(jobs);
  return programs.map((program) => {
    const terminal = terminalJobsByProgramId.get(program.programId);
    if (terminal?.status === 'error') {
      return applyProgramGenJobToProgram(program, terminal);
    }

    if (isProgramGenerationFailed(program)) {
      return program;
    }

    const linkedJobId = program.generationJobId?.trim();
    const linkedJob = linkedJobId
      ? linkedJobsByProgramId.get(program.programId)
      : undefined;
    if (linkedJob?.status === 'error') {
      return applyProgramGenJobToProgram(program, linkedJob);
    }

    const activeJob = activeByProgram.get(program.programId);
    let row = program;

    if (activeJob && isProgramGenJobInFlight(activeJob.status)) {
      row = {
        ...program,
        generationStatus: 'processing' as const,
        generationJobId: activeJob.jobId,
        generationError: undefined,
      };
    } else if (linkedJob?.status === 'done') {
      row = applyProgramGenJobToProgram(program, linkedJob);
    } else if (program.generationStatus === 'processing') {
      row = {
        ...program,
        generationStatus: 'error' as const,
        generationError:
          program.generationError?.trim() ||
          'Program generation stopped before completing.',
        generationJobId: undefined,
      };
    }

    if (terminal?.status === 'done') {
      const applied = applyProgramGenJobToProgram(row, terminal);
      if (
        applied.generationStatus === 'error' ||
        row.generationStatus === 'processing'
      ) {
        return applied;
      }
    }

    return row;
  });
}
